import { useQuery } from "@tanstack/react-query";
import { fetchOpenTrades, fetchRecentTrades } from "../../lib/api.js";
import { TradesTable } from "./TradesTable.js";

/**
 * Live trading dashboard. Shows open + recent live trades. The execution
 * engine refuses to actually place orders unless `EXEC_LIVE_ENABLED=true`,
 * so the tables are usually empty in dev.
 */
export function LiveDashboard() {
  const open = useQuery({
    queryKey: ["live-open"],
    queryFn: () => fetchOpenTrades("LIVE"),
    refetchInterval: 5_000,
  });
  const recent = useQuery({
    queryKey: ["live-recent"],
    queryFn: () => fetchRecentTrades("LIVE", 100),
    refetchInterval: 10_000,
  });

  const closed = recent.data ?? [];
  const totalPnl = closed.reduce((acc, t) => acc + (t.realizedPnl ?? 0), 0);
  const wins = closed.filter((t) => (t.realizedPnl ?? 0) > 0).length;
  const winRate = closed.length === 0 ? 0 : (wins / closed.length) * 100;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto bg-zinc-950 p-4">
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Open positions" value={String(open.data?.length ?? 0)} />
        <Tile
          label="Realized PnL"
          value={`$${totalPnl.toFixed(2)}`}
          tone={totalPnl >= 0 ? "good" : "bad"}
        />
        <Tile
          label="Win rate"
          value={`${winRate.toFixed(1)}%`}
        />
      </section>

      <section className="rounded border border-zinc-800">
        <header className="border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400">
          Open live positions
        </header>
        <TradesTable
          trades={open.data ?? []}
          emptyMessage="No open live positions. Live trading is disabled until EXEC_LIVE_ENABLED=true."
        />
      </section>

      <section className="rounded border border-zinc-800">
        <header className="border-b border-zinc-800 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400">
          Recent live trades
        </header>
        <TradesTable
          trades={closed}
          emptyMessage="No closed live trades yet."
        />
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  const color =
    tone === "good"
      ? "text-accent"
      : tone === "bad"
        ? "text-bear"
        : "text-zinc-200";
  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/40 px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className={`mt-1 font-mono text-2xl ${color}`}>{value}</div>
    </div>
  );
}
